import { Search, User } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";

import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Notification } from "../hooks/useFollowedPodcasts";

import {
  // CreatorsIcon, // Hidden for now (demo phase)
  ClassicRadioIcon,
  PodcastMicIcon,
} from "./icons/NavIcons";
import NotificationDropdown from "./NotificationDropdown";
interface Props {
  themeMode: "dark" | "light";
  isPlaying: boolean;
  activeNav: "landing" | "radio" | "podcasts" | "creators";
  onNavigateStart: () => void;
  onNavigateRadio: () => void;
  onNavigatePodcasts: () => void;
  onNavigateCreators: () => void;
  // onCustomize removed

  onGoToFavorites: () => void;
  onGoToHistory: () => void;
  onToggleProfile: () => void;
  onGoHome: () => void;
  profileTriggerRef: React.RefObject<HTMLButtonElement | null>;
  onSearchClick: () => void;
  isMobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  onCloseMobileMenu: () => void;
  // Notification props
  notifications: Notification[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onClearNotifications: () => void;
  onRemoveNotification: (id: string) => void;
}

const Header: React.FC<Props> = ({
  themeMode,
  isPlaying,
  activeNav,
  onNavigateStart: _onNavigateStart,
  onNavigateRadio,
  onNavigatePodcasts,
  onNavigateCreators,

  onGoToFavorites: _onGoToFavorites,
  onGoToHistory: _onGoToHistory,
  onToggleProfile,
  onGoHome,
  profileTriggerRef,
  onSearchClick,
  isMobileMenuOpen,
  _onOpenMobileMenu,
  onCloseMobileMenu: _onCloseMobileMenu,
  notifications,
  unreadCount,
  onMarkAllRead,
  onClearNotifications,
  onRemoveNotification,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Get formatted user name for greeting
  const _userName = user
    ? user.user_metadata?.username || user.email?.split("@")[0] || "User"
    : null;

  // Long-press state for vinyl "Surprise Me"
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [longPressProgress, setLongPressProgress] = useState(0);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggered = useRef(false);

  const handleVinylMouseDown = () => {
    longPressTriggered.current = false;
    setIsLongPressing(true);
    setLongPressProgress(0);

    // Progress animation (fills over 1 second)
    let progress = 0;
    progressInterval.current = setInterval(() => {
      progress += 5;
      setLongPressProgress(Math.min(progress, 100));
    }, 50);

    // Trigger after 1 second
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // onSurpriseMe(); // Removed

      handleVinylMouseUp();
    }, 1000);
  };

  const handleVinylMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }

    // If released before 1 second and not triggered, go home
    if (!longPressTriggered.current && isLongPressing) {
      onGoHome();
    }

    setIsLongPressing(false);
    setLongPressProgress(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, []);

  // Logo touch state for color chase on hold
  const [isLogoTouched, setIsLogoTouched] = useState(false);
  const logoTouchTimer = useRef<NodeJS.Timeout | null>(null);
  const logoTapTriggered = useRef(false);

  const handleLogoTouchStart = () => {
    logoTapTriggered.current = false;
    logoTouchTimer.current = setTimeout(() => {
      setIsLogoTouched(true);
      logoTapTriggered.current = true;
    }, 100); // Short delay to differentiate tap from hold
  };

  const handleLogoTouchEnd = () => {
    if (logoTouchTimer.current) {
      clearTimeout(logoTouchTimer.current);
      logoTouchTimer.current = null;
    }

    // If it was just a quick tap, go home
    if (!logoTapTriggered.current) {
      onGoHome();
    }

    setIsLogoTouched(false);
  };

  // Animation on initial page load
  useEffect(() => {
    const initialAnimationTimer = setTimeout(() => {
      setIsLogoTouched(true);
      setTimeout(() => setIsLogoTouched(false), 800);
    }, 500); // Small delay after page load
    return () => clearTimeout(initialAnimationTimer);
  }, []);

  // Periodic animation while music is playing (every 30 seconds)
  useEffect(() => {
    if (!isPlaying) return;

    const playbackAnimationInterval = setInterval(() => {
      setIsLogoTouched(true);
      setTimeout(() => setIsLogoTouched(false), 800);
    }, 30000); // Every 30 seconds

    return () => clearInterval(playbackAnimationInterval);
  }, [isPlaying]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="relative z-50 flex-shrink-0 w-full bg-[var(--bg-card)] dark:bg-[#0a0a0a] border-b border-[var(--border-primary)] dark:border-white/5">
      {/* Desktop Header */}
      <div className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-5 hidden md:flex items-center justify-between gap-6">
        {/* LEFT: BRANDING & DJ TRIGGER */}
        <div className="flex items-center gap-3 md:gap-4">
          {/* Vinyl Record - Long-press for Surprise Me */}
          <div
            className="relative w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center transition-transform duration-300 hover:scale-110 cursor-pointer group select-none"
            onMouseDown={handleVinylMouseDown}
            onMouseUp={handleVinylMouseUp}
            onMouseLeave={handleVinylMouseUp}
            onTouchStart={handleVinylMouseDown}
            onTouchEnd={handleVinylMouseUp}
            role="button"
            aria-label="Hold for Surprise Me"
          >
            {/* Long-press progress ring */}
            {isLongPressing && (
              <svg className="absolute inset-0 w-full h-full -rotate-90 z-30 pointer-events-none">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${longPressProgress * 2.83} 283`}
                  className="transition-all duration-75"
                />
              </svg>
            )}

            {/* Outer glow when playing or long-pressing */}
            <div
              className={`absolute inset - 0 rounded - full blur - md transition - opacity duration - 500 ${
                isLongPressing
                  ? "bg-[#22d3ee]/40 opacity-100"
                  : isPlaying
                    ? "bg-[#22d3ee]/20 opacity-60"
                    : "opacity-0 bg-[#22d3ee]/20"
              } `}
            ></div>

            <div
              className="relative w-full h-full rounded-full border border-gray-300/50 dark:border-white/10 animate-[spin_4s_linear_infinite] bg-gradient-to-br from-gray-100 to-white dark:from-[#1a1a1a] dark:to-[#0a0a0a]"
              style={{
                animationPlayState: isPlaying ? "running" : "paused",
                boxShadow:
                  "0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
              }}
            >
              {/* Vinyl Grooves - Softer, multi-layered for organic feel */}
              <div
                className="absolute inset-[3px] rounded-full opacity-[0.08] dark:opacity-[0.15]"
                style={{
                  background:
                    "radial-gradient(circle, transparent 30%, currentColor 31%, transparent 32%, transparent 40%, currentColor 41%, transparent 42%, transparent 50%, currentColor 51%, transparent 52%, transparent 60%, currentColor 61%, transparent 62%, transparent 70%, currentColor 71%, transparent 72%)",
                }}
              ></div>

              {/* Secondary subtle grooves */}
              <div
                className="absolute inset-[2px] rounded-full opacity-[0.04] dark:opacity-[0.08]"
                style={{
                  background:
                    "radial-gradient(circle, transparent 35%, currentColor 36%, transparent 37%, transparent 55%, currentColor 56%, transparent 57%, transparent 75%, currentColor 76%, transparent 77%)",
                }}
              ></div>

              {/* Shimmer reflection - slow rotating highlight */}
              <div
                className="absolute inset-0 rounded-full animate-[spin_8s_linear_infinite]"
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.08) 20deg, rgba(255,255,255,0.15) 40deg, rgba(255,255,255,0.08) 60deg, transparent 80deg, transparent 360deg)",
                  animationPlayState: isPlaying ? "running" : "paused",
                }}
              ></div>

              {/* Center Label - Glassmorphism with inner shadow */}
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div
                  className="w-[52%] h-[52%] rounded-full flex items-center justify-center backdrop-blur-[2px] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-[#2a2a2a] dark:to-[#1a1a1a]"
                  style={{
                    boxShadow:
                      themeMode === 'dark' 
                        ? "inset 0 2px 4px rgba(0,0,0,0.4), inset 0 -1px 2px rgba(255,255,255,0.1), 0 1px 3px rgba(0,0,0,0.4)"
                        : "inset 0 2px 4px rgba(0,0,0,0.1), inset 0 -1px 2px rgba(255,255,255,0.8), 0 1px 3px rgba(0,0,0,0.2)",
                  }}
                >
                  <span
                    className="se-logo text-[12px] md:text-[16px] font-black tracking-tighter leading-none font-display select-none text-gray-900 dark:text-white"
                    style={{
                      textShadow:
                        "0 0 8px rgba(34, 211, 238, 0.5), 0 0 16px rgba(34, 211, 238, 0.3)",
                    }}
                  >
                    SE
                  </span>
                </div>
              </div>

              {/* Spindle - Metallic look */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full z-20"
                style={{
                  background:
                    "linear-gradient(135deg, #4a4a4a 0%, #1a1a1a 50%, #3a3a3a 100%)",
                  boxShadow:
                    "inset 0 1px 2px rgba(0,0,0,0.8), 0 0.5px 0 rgba(255,255,255,0.2)",
                }}
              ></div>
            </div>

            {/* Hover Hint */}
            <div className="absolute -bottom-2 -right-2 bg-radio-accent/90 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg pointer-events-none">
              Hold
            </div>
          </div>

          {/* Text Logo - Resets to Home with Color Chase Animation */}
          <div
            className="flex flex-col justify-center cursor-pointer relative"
            onClick={onGoHome}
            onTouchStart={(e) => {
              e.preventDefault();
              handleLogoTouchStart();
            }}
            onTouchEnd={handleLogoTouchEnd}
            onTouchCancel={handleLogoTouchEnd}
            role="button"
            aria-label="Go to Home"
          >
            <div className="flex items-center gap-2">
              {/* Logo text with its own hover group */}
              <h1 className="text-base md:text-xl font-bold tracking-tighter leading-none font-display flex items-center gap-0 group/logotext">
                {"STREAMEATER".split("").map((letter, i) => (
                  <span
                    key={i}
                    className={`inline-block transition-colors duration-200 group-hover/logotext:!text-[#22d3ee] ${isLogoTouched ? "!text-[#22d3ee]" : ""}`}
                    style={{
                      transitionDelay: `${i * 40}ms`,
                      color: themeMode === "dark" ? "#ffffff" : "#000000",
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </h1>

              {/* NAV TABS - separate from logo hover group */}
              <nav
                className="hidden md:flex items-center gap-4 ml-4"
                role="navigation"
                onClick={(e) => e.stopPropagation()}
              >
                {[
                  {
                    key: "creators" as const,
                    label: "CREATORS CHOICE",
                    onClick: onNavigateCreators,
                  },
                  {
                    key: "radio" as const,
                    label: t("header.radio").toUpperCase(),
                    onClick: onNavigateRadio,
                  },
                  {
                    key: "podcasts" as const,
                    label: t("header.podcasts").toUpperCase(),
                    onClick: onNavigatePodcasts,
                  },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      item.onClick();
                    }}
                    className="relative group flex items-center justify-center p-1"
                    type="button"
                  >
                    <span
                      className={`text-xs font-bold font-display tracking-widest uppercase transition-colors ${
                        activeNav === item.key
                          ? "text-white"
                          : "text-neutral-500 hover:text-white"
                      }`}
                    >
                      {item.label}
                    </span>

                    {activeNav === item.key && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-0.5 bg-radio-accent rounded-full"></div>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            <span className="hidden md:block text-[8px] text-[var(--text-secondary)] font-mono tracking-[0.5em] uppercase transition-colors pl-0.5">
              {t("header.worldwide_tuner").toUpperCase()}
            </span>
          </div>
        </div>

        {/* CENTER: Empty spacer for layout balance */}
        <div className="flex-1"></div>

        {/* RIGHT: SEARCH & PROFILE */}
        <div className="flex items-center gap-2 md:gap-3 relative z-10">
          {/* Search Button */}
          <button
            onClick={onSearchClick}
            className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] flex items-center justify-center text-gray-900 dark:text-[var(--text-secondary)] hover:text-radio-accent hover:bg-[var(--bg-surface-glass-hover)] hover:border-radio-accent/50 transition-all"
            title="Smart Search"
            aria-label="Search"
          >
            <Search className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </button>

          {/* Notifications Bell */}
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={onMarkAllRead}
            onClearAll={onClearNotifications}
            onRemoveNotification={onRemoveNotification}
          />

          {/* Profile */}
          <button
            ref={profileTriggerRef}
            onClick={onToggleProfile}
            className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] flex items-center justify-center text-gray-900 dark:text-[var(--text-primary)] hover:text-radio-accent hover:bg-[var(--bg-surface-glass-hover)] hover:border-radio-accent/50 transition-all"
            aria-label="Profile"
          >
            <User className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </button>
        </div>
      </div>

      {/* MOOD SELECTOR ROW (Desktop - Only on Radio) */}

      {/* Mobile Header - Clean, Premium Design */}
      <div className="md:hidden px-3 py-2.5 flex items-center justify-between w-full bg-[var(--bg-primary)]/95 backdrop-blur-md border-b border-[var(--glass-border)]/50">
        {/* LEFT: Vinyl + Nav Icons grouped together */}
        <div className="flex items-center gap-3">
          {/* Vinyl Logo */}
          <div
            className="relative w-10 h-10 flex-shrink-0 cursor-pointer"
            onMouseDown={handleVinylMouseDown}
            onMouseUp={handleVinylMouseUp}
            onMouseLeave={handleVinylMouseUp}
            onTouchStart={handleVinylMouseDown}
            onTouchEnd={handleVinylMouseUp}
            role="button"
            aria-label="Home"
          >
            {/* Long-press progress ring */}
            {isLongPressing && (
              <svg className="absolute inset-0 w-full h-full -rotate-90 z-30 pointer-events-none">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${longPressProgress * 2.26} 226`}
                  className="transition-all duration-75"
                />
              </svg>
            )}

            {/* Glow effect */}
            <div
              className={`absolute inset-0 rounded-full blur-md transition-opacity duration-500 ${
                isLongPressing
                  ? "bg-[#22d3ee]/40 opacity-100"
                  : isPlaying
                    ? "bg-[#22d3ee]/30 opacity-70"
                    : "opacity-0"
              }`}
            />

            {/* Vinyl disc */}
            <div
              className="relative w-full h-full rounded-full border border-white/10 animate-[spin_4s_linear_infinite] bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]"
              style={{
                animationPlayState: isPlaying ? "running" : "paused",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
              }}
            >
              {/* Grooves */}
              <div
                className="absolute inset-[3px] rounded-full opacity-20"
                style={{
                  background:
                    "radial-gradient(circle, transparent 30%, currentColor 31%, transparent 32%, transparent 50%, currentColor 51%, transparent 52%, transparent 70%, currentColor 71%, transparent 72%)",
                }}
              />
              {/* Center label */}
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div
                  className="w-[50%] h-[50%] rounded-full flex items-center justify-center bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a]"
                  style={{
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.3)",
                  }}
                >
                  <span
                    className="text-[10px] font-black tracking-tighter text-white"
                    style={{
                      textShadow: "0 0 6px rgba(34, 211, 238, 0.6)",
                    }}
                  >
                    SE
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Nav Icons - right next to vinyl */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={onNavigateRadio}
              className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                activeNav === "radio"
                  ? "text-radio-accent bg-radio-accent/15"
                  : "bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
              style={activeNav === "radio" ? { boxShadow: "0 0 12px rgba(34, 211, 238, 0.4)" } : undefined}
              aria-label="Radio"
            >
              <ClassicRadioIcon size={18} />
            </button>
            <button
              onClick={onNavigatePodcasts}
              className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                activeNav === "podcasts"
                  ? "text-radio-accent bg-radio-accent/15"
                  : "bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
              style={activeNav === "podcasts" ? { boxShadow: "0 0 12px rgba(34, 211, 238, 0.4)" } : undefined}
              aria-label="Podcasts"
            >
              <PodcastMicIcon size={18} />
            </button>
          </div>
        </div>

        {/* RIGHT: Actions */}
        <div className="flex items-center gap-1.5">
          {/* Search */}
          <button
            onClick={onSearchClick}
            className="w-9 h-9 rounded-full bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] flex items-center justify-center text-[var(--text-primary)] hover:text-radio-accent transition-all"
            aria-label="Search"
          >
            <Search className="w-4 h-4" />
          </button>

          {/* Notifications */}
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={onMarkAllRead}
            onClearAll={onClearNotifications}
            onRemoveNotification={onRemoveNotification}
          />

          {/* Profile */}
          <button
            onClick={onToggleProfile}
            className="w-9 h-9 rounded-full bg-[var(--bg-surface-glass)] border border-[var(--glass-border)] flex items-center justify-center text-[var(--text-primary)] hover:text-radio-accent hover:border-radio-accent/50 transition-all"
            aria-label="Profile"
          >
            <User className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* MOOD SELECTOR ROW (Mobile - Only on Radio) - MOVED HERE */}
    </div>
  );
};

export default Header;
